import { Link } from '@tanstack/react-router'
import { ArrowRightIcon, CheckIcon, MailIcon, PhoneIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { FacebookIcon } from '@/components/icons/facebook'
import { InstagramIcon } from '@/components/icons/instagram'
import { TiktokIcon } from '@/components/icons/tiktok'
import { Button } from '@/components/ui/button'
import { LandingLink } from '@/features/landing/components/LandingLink'
import { getBasePageBySlug } from '@/features/landing/registry'
import { useNewsletterForm } from '@/features/newsletter/useNewsletterForm'
import { suppressNewsletterPopup } from '@/features/newsletter/useNewsletterPopup'
import { cn } from '@/lib/utils'

const regionSlugs = ['krkonose', 'sumava', 'jizni-cechy', 'beskydy-a-valassko', 'jeseniky', 'jizerske-hory']

const facilitySlugs = ['se-saunou', 's-bazenem', 's-virivkou', 's-krbem', 'pro-psy', 'pro-deti']

const categorySlugs = ['chalupy', 'chaty', 'roubenky', 'apartmany', 'vily', 'glamping']

// Keep in sync with the Organization `sameAs` list in `src/lib/seo/siteMeta.ts`.
const socialProfiles = [
  {
    href: 'https://www.facebook.com/matevolno/',
    label: 'Mátevolno na Facebooku',
    icon: FacebookIcon,
  },
  {
    href: 'https://www.instagram.com/matevolno/',
    label: 'Mátevolno na Instagramu',
    icon: InstagramIcon,
  },
  {
    href: 'https://www.tiktok.com/@matevolno',
    label: 'Mátevolno na TikToku',
    icon: TiktokIcon,
  },
]

const legalDocuments = [
  { href: '/docs/matevolno_vop_b2c.pdf', label: 'Obchodní podmínky' },
  { href: '/docs/matevolno_vop.pdf', label: 'Obchodní podmínky pro podniky' },
  { href: '/docs/matevolno_reklamacni_rad.pdf', label: 'Reklamační řád' },
  { href: '/docs/matevolno_gdpr.pdf', label: 'Zpracování osobních údajů' },
  { href: '/docs/matevolno_cookies.pdf', label: 'Cookies' },
]

const linkClass =
  'rounded-sm text-slate-400 transition-colors hover:text-white focus-visible:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/50'

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-4 text-xs font-semibold tracking-[0.12em] text-white uppercase">{title}</h3>
      <ul className="space-y-2.5 text-sm">{children}</ul>
    </div>
  )
}

function LandingColumn({ title, slugs }: { title: string; slugs: string[] }) {
  const pages = slugs.flatMap((slug) => getBasePageBySlug(slug) ?? [])

  return (
    <FooterColumn title={title}>
      {pages.map((page) => (
        <li key={page.slug}>
          <LandingLink page={page} className={linkClass}>
            {page.linkLabel}
          </LandingLink>
        </li>
      ))}
    </FooterColumn>
  )
}

function FooterNewsletter() {
  // Signing up here also silences the popup — no point nagging a subscriber.
  const { email, setEmail, status, submit } = useNewsletterForm(suppressNewsletterPopup)

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm md:flex md:items-center md:justify-between md:gap-12 md:p-8">
      <div className="max-w-md">
        <p className="text-xs font-semibold tracking-[0.14em] text-red-400 uppercase">Tipy na víkend zdarma</p>
        <p className="mt-2 text-xl font-semibold text-white">Plány na víkend vyřešené</p>
        <p className="mt-1 text-sm text-slate-400">
          Ručně vybrané chalupy a wellness pobyty na poslední chvíli. Jeden e-mail týdně, kdykoliv se odhlásíte.
        </p>
      </div>

      {status === 'success' ? (
        <p className="mt-5 flex items-center gap-2 text-sm font-medium text-white md:mt-0 md:w-80 md:shrink-0">
          <CheckIcon className="h-5 w-5 shrink-0 text-green-400" />
          Jste v klubu — první tipy dorazí ve středu.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-5 md:mt-0 md:w-80 md:shrink-0">
          <div className="flex flex-col gap-2 sm:flex-row">
            <label htmlFor="footer-newsletter-email" className="sr-only">
              E-mail
            </label>
            <input
              id="footer-newsletter-email"
              type="email"
              name="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Váš e-mail"
              className="h-11 w-full rounded-xl border border-white/15 bg-white/10 px-4 text-sm text-white placeholder-slate-400 transition-colors focus:border-white/40 focus:outline-none"
            />
            <Button
              type="submit"
              variant="red"
              size="lg"
              className="w-full shrink-0 text-base sm:w-auto"
              disabled={status === 'submitting'}
            >
              Chci tipy
              <ArrowRightIcon className="h-4 w-4" />
            </Button>
          </div>
          {status === 'invalid-email' && (
            <p className="mt-2 text-xs text-red-400">Zkontrolujte prosím e-mailovou adresu.</p>
          )}
          {status === 'error' && (
            <p className="mt-2 text-xs text-red-400">Něco se nepovedlo, zkuste to prosím znovu.</p>
          )}
        </form>
      )}
    </div>
  )
}

export default function Footer() {
  // The top margin is the footer's own breathing room: page sections end flush
  // (several have no bottom spacing at all), so without it the dark panel butts
  // straight against the last line of copy.
  return (
    <footer className="relative isolate mt-16 overflow-hidden bg-gradient-to-b from-slate-900 to-slate-950 pb-[env(safe-area-inset-bottom)] md:mt-24">
      {/* Soft brand-red glow behind the newsletter panel, so the top of the
          footer has depth instead of reading as a flat dark block. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-[radial-gradient(70%_100%_at_50%_0%,rgba(244,18,62,0.16),transparent_70%)]"
      />

      {/* Google was assembling the search snippet out of this footer (phone,
          e-mail, legal links) instead of the meta description, because the
          pages above it are short. `data-nosnippet` takes the block out of
          snippet selection without hiding it from indexing or ranking. */}
      <div className="container pt-14 pb-8 md:pt-20" data-nosnippet>
        <FooterNewsletter />

        <div className="mt-12 grid grid-cols-2 gap-x-8 gap-y-10 md:mt-16 md:grid-cols-4">
          <LandingColumn title="Kam vyrazit" slugs={regionSlugs} />
          <LandingColumn title="Podle vybavení" slugs={facilitySlugs} />
          <LandingColumn title="Typy ubytování" slugs={categorySlugs} />
          <FooterColumn title="Mátevolno">
            <li>
              <Link to="/ubytovani/hledat" className={linkClass}>
                Nabídky ubytování
              </Link>
            </li>
            <li>
              <Link to="/ubytovani" className={linkClass}>
                Všechna ubytování
              </Link>
            </li>
            <li>
              <Link to="/pobyty" className={linkClass}>
                Tipy na pobyty
              </Link>
            </li>
            <li>
              <Link to="/" hash="about" className={linkClass}>
                Jak to funguje
              </Link>
            </li>
            <li>
              <Link to="/inspirace" className={linkClass}>
                Inspirace
              </Link>
            </li>
            <li>
              <Link to="/partner" className={linkClass}>
                Přidat ubytování
              </Link>
            </li>
            <li>
              <Link to="/kontakt" className={linkClass}>
                Kontakt
              </Link>
            </li>
          </FooterColumn>
        </div>

        <div className="mt-12 flex flex-col gap-8 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
            <Link to="/" aria-label="Mátevolno – domů" className="shrink-0">
              <img src="/img/logo_matevolno_noshadow.svg" alt="Mátevolno" className="-my-4 h-[70px] w-auto" />
            </Link>
            <p className="max-w-[13rem] text-xs leading-tight font-medium text-slate-400 sm:border-l sm:border-white/15 sm:pl-5">
              Last-minute úniky na příštích 14 dní.
            </p>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
            <div className="flex flex-col gap-2 text-sm font-medium">
              <a href="tel:+420605131713" className={cn(linkClass, 'flex items-center gap-2')}>
                <PhoneIcon className="h-4 w-4 shrink-0" />
                +420 605 131 713
              </a>
              <a href="mailto:info@matevolno.cz" className={cn(linkClass, 'flex items-center gap-2')}>
                <MailIcon className="h-4 w-4 shrink-0" />
                info@matevolno.cz
              </a>
            </div>
            <div className="flex gap-3">
              {socialProfiles.map((profile) => (
                <a
                  key={profile.href}
                  href={profile.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={profile.label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-white/40 hover:text-white"
                >
                  <profile.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs text-slate-500 lg:flex-row lg:items-center lg:justify-between">
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {legalDocuments.map((document) => (
              <li key={document.href}>
                <a href={document.href} target="_blank" rel="noreferrer" className={linkClass}>
                  {document.label}
                </a>
              </li>
            ))}
          </ul>
          <p>© 2026 První chalupářská s.r.o., IČO 19626487. Všechna práva vyhrazena.</p>
        </div>
      </div>
    </footer>
  )
}
